import type { JSX } from 'react';
import { useEffect, useState } from 'react';

import { Info, RotateCcw } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import { useCreateRunMutation } from '../../api/runs';
import {
  AGENT_CALLS_PER_SCENARIO,
  JUDGE_CALLS_PER_SCENARIO,
  formatUsd,
  familyDisplayName,
  runPromptVersionLabel,
  type AgentTestRun,
} from '../../model/run';

type ReRunVersion = 'original' | 'production' | 'latest';

const RERUN_ERROR_FALLBACK = 'No se pudo crear el re-run. Intenta nuevamente.';

interface ReRunDialogProps {
  run: AgentTestRun | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReRunDialog = ({ run, open, onOpenChange }: ReRunDialogProps): JSX.Element | null => {
  const [version, setVersion] = useState<ReRunVersion>('original');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createRun, { isLoading }] = useCreateRunMutation();

  useEffect(() => {
    if (open) {
      setVersion('original');
      setSubmitError(null);
    }
  }, [open]);

  if (!run) return null;

  const name = familyDisplayName(run);
  const familyName = run.rootRunId === null ? run.name : run.rootName ?? null;

  const handleReRun = async (): Promise<void> => {
    setSubmitError(null);
    try {
      await createRun({
        ...(familyName && familyName.trim().length > 0 ? { name: familyName } : {}),
        projectId: run.projectId,
        subProjectId: run.subProjectId,
        promptLabel: version === 'original' ? run.promptLabel : version,
        model: run.model,
        reRunOfRunId: run.id,
        scenarios: run.scenarioIds,
      }).unwrap();
      onOpenChange(false);
    } catch (err) {
      setSubmitError(extractErrorMessage(err, RERUN_ERROR_FALLBACK));
    }
  };

  const options: { value: ReRunVersion; title: string; description: string }[] = [
    {
      value: 'original',
      title: `Misma versión del run original — ${runPromptVersionLabel(run)}`,
      description: 'Reproduce el run tal como corrió la última vez, para comparar resultados.',
    },
    {
      value: 'production',
      title: 'Producción',
      description: 'La versión que está sirviendo a los clientes hoy.',
    },
    {
      value: 'latest',
      title: 'Latest',
      description: 'La última versión guardada, para validar cambios antes de promoverla.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="size-4" aria-hidden />
            Re-run del test
          </DialogTitle>
          <DialogDescription>
            Vas a relanzar «{name}» con los mismos escenarios. Elige con qué versión del prompt correr.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {options.map((option) => {
            const isSelected = version === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setVersion(option.value)}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                  isSelected ? 'border-brand-vivid bg-brand-vivid/5 ring-1 ring-brand-vivid' : 'border-border-subtle'
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2',
                    isSelected ? 'border-brand-vivid' : 'border-border-default'
                  )}
                >
                  {isSelected ? <span className="size-2 rounded-full bg-brand-vivid" /> : null}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-text-default">{option.title}</span>
                  <span className="text-xs text-text-muted">{option.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 rounded-md border border-brand-vivid/30 bg-surface-info px-3 py-2">
          <Info className="size-4 shrink-0 text-brand-vivid" aria-hidden />
          <p className="m-0 text-sm text-text-default">
            {run.scenarioIds.length} esc — ~{run.scenarioIds.length * AGENT_CALLS_PER_SCENARIO} llamadas al agente + ~
            {run.scenarioIds.length * JUDGE_CALLS_PER_SCENARIO} de juez · ref. run anterior:{' '}
            {formatUsd(run.totalCostUsd)}
          </p>
        </div>

        {submitError ? (
          <p className="m-0 rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error" role="alert">
            {submitError}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleReRun()} disabled={isLoading}>
            <RotateCcw className="size-4" aria-hidden />
            {isLoading ? 'Creando…' : 'Correr re-run'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReRunDialog;
